// web/src/pages/search-results.js
//
// The parts of the search page that are decisions rather than markup: what the summary
// line says, where a result links, and the search's two addresses -- the one in the
// browser's address bar (/support/search?q=), which the header's search box writes and the page
// reads, and the GET /api/search request the page sends (docs/API.md §3.8).

import { supportPath } from '../support-path.js';

/** The API's default page size, sent explicitly as the queue sends its own. */
export const PAGE_SIZE = 25;

/**
 * The API's cap on q (docs/API.md §3.8), as every search box's maxLength, so typing alone
 * cannot produce a 400.
 */
export const MAX_QUERY_LENGTH = 200;

// What GET /api/search refuses in `q` (UNSAFE_TEXT in src/lib/validate.js): C0 and C1
// controls, the line and paragraph separators, and the bidi embeddings, overrides and
// isolates. A pasted tab is the realistic one. None of them is a letter, a digit or an
// underscore, so the search already splits words on every one of them
// (src/search/query.js), and a space in their place searches for exactly what was meant.
const REFUSED_IN_QUERY = /[\u0000-\u001F\u007F-\u009F\u2028\u2029\u202A-\u202E\u2066-\u2069]/g;

/** Typed or pasted text with the refused characters replaced and the ends trimmed; '' when nothing is left. */
function cleanQuery(input) {
  return typeof input === 'string' ? input.replace(REFUSED_IN_QUERY, ' ').trim() : '';
}

/**
 * "26-50 of 310", or a plain sentence when this page has nothing on it.
 *
 * The third argument is the envelope's `page`; only its offset is read.
 */
export function summarizeResults(rows, total, { offset }) {
  const count = Array.isArray(rows) ? rows.length : 0;
  if (count === 0 || total === 0) {
    // A page past the end -- reached by a stale link -- has no rows but a true total
    // (the API counts it separately), and must not read as a search that found nothing.
    if (total > 0) {
      return `${total.toLocaleString()} matching ${total === 1 ? 'ticket' : 'tickets'}, none on this page`;
    }
    return 'No matching tickets';
  }
  const first = offset + 1;
  // Count the rows actually returned, not offset+limit: the last page is short and
  // claiming 26-50 when only 27 exist reads as results that failed to render.
  const last = offset + count;
  return `${first.toLocaleString()}-${last.toLocaleString()} of ${total.toLocaleString()}`;
}

/** Tickets are addressed by their public ref, which is the Zendesk number for a ported ticket. */
export function resultHref(row) {
  return supportPath(`/tickets/${row.public_ref}`);
}

/**
 * The address of a search, or null when the input leaves nothing to search for.
 *
 * Built with URLSearchParams, so a typed # or & is encoded rather than read as the start
 * of a fragment or of another parameter. The first page carries no offset.
 */
export function searchHref(input, offset = 0) {
  const q = cleanQuery(input);
  if (q === '') return null;
  const params = new URLSearchParams({ q });
  if (offset > 0) params.set('offset', String(offset));
  return supportPath(`/search?${params}`);
}

/**
 * What submitting a search box does: `{ href, replace }`, or null when the input leaves
 * nothing to search for. `here` is the address the browser is on (pathname + search).
 *
 * `replace` is true when the search is the one already showing. Pressing Enter on it
 * again is a real request -- a message becomes searchable seconds after it is written
 * (docs/API.md §3.8) -- so the page fetches on every navigation, and replacing the entry
 * runs the search again without stacking a duplicate of it in Back's history.
 */
export function searchSubmission(input, here) {
  const href = searchHref(input);
  if (href === null) return null;
  return { href, replace: href === here };
}

/**
 * The search the address bar describes. `q` is cleaned the way searchHref cleans it, and
 * a negative or non-numeric offset, which the API would refuse (docs/API.md §1.5), reads
 * as the first page.
 */
export function readSearch(searchParams) {
  const offset = Number.parseInt(searchParams.get('offset') ?? '0', 10);
  return {
    q: cleanQuery(searchParams.get('q')),
    offset: Number.isFinite(offset) && offset > 0 ? offset : 0,
  };
}

/**
 * The path, under the client's /api, of one page of results. Only the three parameters
 * docs/API.md §3.8 documents: any other parameter is a 400.
 */
export function searchApiPath({ q, offset }) {
  const params = new URLSearchParams({ q, limit: String(PAGE_SIZE) });
  if (offset > 0) params.set('offset', String(offset));
  return `/search?${params}`;
}
